import React, { useState } from "react";
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useTheme } from "../context/ThemeContext";

const ServiceCard = ({ title, description, status, icon, features, onConfigure }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	return (
		<View style={styles.serviceCard}>
			<TouchableOpacity style={styles.serviceHeader} onPress={() => setIsExpanded(!isExpanded)}>
				<View style={styles.serviceHeaderLeft}>
					<View style={styles.serviceIcon}>
						<Ionicons name={icon} size={24} color="#82C91E" />
					</View>
					<View>
						<Text style={styles.serviceTitle}>{title}</Text>
						<Text style={styles.serviceDescription}>{description}</Text>
					</View>
				</View>
				<View style={styles.serviceHeaderRight}>
					<View style={[styles.statusBadge, status === "active" ? styles.statusActive : status === "beta" ? styles.statusBeta : styles.statusComingSoon]}>
						<Text style={styles.statusText}>{status === "active" ? "Active" : status === "beta" ? "Beta" : "Coming Soon"}</Text>
					</View>
					<TouchableOpacity style={styles.configureButton} onPress={onConfigure}>
						<Ionicons name="settings-outline" size={20} color="#666" />
					</TouchableOpacity>
					<Ionicons name={isExpanded ? "chevron-up" : "chevron-down"} size={20} color="#666" />
				</View>
			</TouchableOpacity>

			{isExpanded && (
				<View style={styles.serviceContent}>
					<View style={styles.featuresList}>
						{features.map((feature, index) => (
							<View key={index} style={styles.featureItem}>
								<Ionicons name={feature.icon} size={20} color="#82C91E" />
								<View style={styles.featureInfo}>
									<Text style={styles.featureTitle}>{feature.title}</Text>
									<Text style={styles.featureDescription}>{feature.description}</Text>
								</View>
							</View>
						))}
					</View>
					<View style={styles.serviceActions}>
						<TouchableOpacity style={styles.actionButton}>
							<Ionicons name="document-text-outline" size={20} color="#fff" />
							<Text style={styles.actionButtonText}>Documentation</Text>
						</TouchableOpacity>
						<TouchableOpacity style={[styles.actionButton, styles.actionButtonPrimary]}>
							<Ionicons name="rocket-outline" size={20} color="#000" />
							<Text style={styles.actionButtonTextPrimary}>Quick Start</Text>
						</TouchableOpacity>
					</View>
				</View>
			)}
		</View>
	);
};

const Services = () => {
	const { appId } = useParams();
	const theme = useTheme();
	const [activeTab, setActiveTab] = useState("core");

	const services = {
		core: [
			{
				title: "Accounts",
				description: "Complete user management system with roles and permissions",
				status: "active",
				icon: "people-circle-outline",
				features: [
					{
						title: "Role Management",
						description: "Create and manage custom roles with granular permissions",
						icon: "shield-checkmark-outline",
					},
					{
						title: "User Moderation",
						description: "Tools for banning, muting, and managing user access",
						icon: "hammer-outline",
					},
					{
						title: "Subscription Roles",
						description: "Automatically assign roles based on subscription status",
						icon: "key-outline",
					},
					{
						title: "Analytics",
						description: "Track user engagement and role distribution",
						icon: "analytics-outline",
					},
				],
			},
			{
				title: "Chatterbox",
				description: "Real-time messaging and discussion platform",
				status: "beta",
				icon: "chatbubbles-outline",
				features: [
					{
						title: "Real-time Chat",
						description: "Instant messaging with presence indicators",
						icon: "time-outline",
					},
					{
						title: "Rich Media",
						description: "Share images, files, and rich media content",
						icon: "images-outline",
					},
					{
						title: "Thread Support",
						description: "Organize discussions with threaded replies",
						icon: "git-branch-outline",
					},
					{
						title: "Moderation Tools",
						description: "Advanced tools for content moderation",
						icon: "shield-outline",
					},
				],
			},
			{
				title: "Payments",
				description: "Secure payment processing and subscription management",
				status: "coming_soon",
				icon: "card-outline",
				features: [
					{
						title: "Multiple Providers",
						description: "Support for major payment providers",
						icon: "wallet-outline",
					},
					{
						title: "Subscription Management",
						description: "Handle recurring payments and subscriptions",
						icon: "refresh-outline",
					},
					{
						title: "Payment Analytics",
						description: "Track revenue and payment metrics",
						icon: "stats-chart-outline",
					},
					{
						title: "Fraud Protection",
						description: "Advanced fraud detection and prevention",
						icon: "shield-checkmark-outline",
					},
				],
			},
		],
	};

	const handleConfigure = (service) => {
		// Handle service configuration
		console.log("Configure", service);
	};

	return (
		<View style={[styles.container, { backgroundColor: theme.colors.background }]}>
			<View style={styles.header}>
				<Text style={styles.headerTitle}>Services</Text>
				<Text style={styles.headerDescription}>Manage and configure your app services</Text>
			</View>

			<View style={styles.tabs}>
				<TouchableOpacity style={[styles.tab, activeTab === "core" && styles.activeTab]} onPress={() => setActiveTab("core")}>
					<Ionicons name="cube-outline" size={20} color={activeTab === "core" ? "#82C91E" : "#666"} />
					<Text style={[styles.tabText, activeTab === "core" && styles.activeTabText]}>Core Services</Text>
				</TouchableOpacity>
				<TouchableOpacity style={[styles.tab, activeTab === "addons" && styles.activeTab]} onPress={() => setActiveTab("addons")}>
					<Ionicons name="extension-puzzle-outline" size={20} color={activeTab === "addons" ? "#82C91E" : "#666"} />
					<Text style={[styles.tabText, activeTab === "addons" && styles.activeTabText]}>Add-ons</Text>
				</TouchableOpacity>
			</View>

			<ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
				<View style={styles.servicesList}>
					{services[activeTab].map((service, index) => (
						<ServiceCard key={index} {...service} onConfigure={() => handleConfigure(service)} />
					))}
				</View>
			</ScrollView>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#1A1A1A",
		minHeight: "100vh",
		display: "flex",
		flexDirection: "column",
	},
	header: {
		padding: 24,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	headerTitle: {
		fontSize: 24,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 8,
	},
	headerDescription: {
		fontSize: 14,
		color: "#666",
	},
	tabs: {
		flexDirection: "row",
		padding: 16,
		gap: 8,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	tab: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 8,
		paddingHorizontal: 16,
		borderRadius: 8,
	},
	activeTab: {
		backgroundColor: "#2A2A2A",
	},
	tabText: {
		color: "#666",
		fontSize: 14,
	},
	activeTabText: {
		color: "#82C91E",
	},
	content: {
		flex: 1,
		padding: 24,
		overflow: "auto",
	},
	servicesList: {
		flex: 1,
		gap: 16,
	},
	serviceCard: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		overflow: "hidden",
	},
	serviceHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 20,
	},
	serviceHeaderLeft: {
		flexDirection: "row",
		alignItems: "center",
		gap: 16,
	},
	serviceIcon: {
		width: 48,
		height: 48,
		borderRadius: 12,
		backgroundColor: "rgba(130, 201, 30, 0.1)",
		justifyContent: "center",
		alignItems: "center",
	},
	serviceTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 4,
	},
	serviceDescription: {
		fontSize: 14,
		color: "#666",
		maxWidth: 300,
	},
	serviceHeaderRight: {
		flexDirection: "row",
		alignItems: "center",
		gap: 16,
	},
	statusBadge: {
		paddingHorizontal: 12,
		paddingVertical: 6,
		borderRadius: 16,
	},
	statusActive: {
		backgroundColor: "rgba(130, 201, 30, 0.1)",
	},
	statusBeta: {
		backgroundColor: "rgba(255, 146, 43, 0.1)",
	},
	statusComingSoon: {
		backgroundColor: "rgba(76, 110, 245, 0.1)",
	},
	statusText: {
		fontSize: 12,
		fontWeight: "600",
		color: "#82C91E",
	},
	configureButton: {
		padding: 8,
	},
	serviceContent: {
		padding: 20,
		borderTopWidth: 1,
		borderTopColor: "#333",
	},
	featuresList: {
		gap: 16,
	},
	featureItem: {
		flexDirection: "row",
		gap: 12,
	},
	featureInfo: {
		flex: 1,
	},
	featureTitle: {
		fontSize: 14,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 4,
	},
	featureDescription: {
		fontSize: 13,
		color: "#666",
	},
	serviceActions: {
		flexDirection: "row",
		gap: 8,
		marginTop: 24,
	},
	actionButton: {
		flex: 1,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#333",
	},
	actionButtonPrimary: {
		backgroundColor: "#82C91E",
	},
	actionButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	actionButtonTextPrimary: {
		color: "#000",
		fontSize: 14,
		fontWeight: "600",
	},
});

export default Services;
